import { useCallback, useMemo, useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useCategories } from '@/hooks/useCategories';
import { usePlans } from '@/hooks/usePlans';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface ChannelFiltersProps {
  providerId: number;
  versionId: number;
  className?: string;
  filters: {
    search: string;
    categoryId: number | undefined;
    planId: number | undefined;
    isLocal: boolean | undefined;
    isDuplicate: boolean | undefined;
  };
  onFiltersChange: (filters: {
    search: string;
    categoryId: number | undefined;
    planId: number | undefined;
    isLocal: boolean | undefined;
    isDuplicate: boolean | undefined;
  }) => void;
}

export function ChannelFilters({
  providerId,
  versionId,
  className,
  filters,
  onFiltersChange
}: ChannelFiltersProps) {
  const { data: categories } = useCategories(providerId, versionId);
  const { data: plans } = usePlans(providerId, versionId);
  const [searchValue, setSearchValue] = useState(filters.search);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        onFiltersChange({
          ...filters,
          search: searchValue
        });
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchValue, filters, onFiltersChange]);

  // Sync state when filters change externally
  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  // Build category hierarchy
  const categoryHierarchy = useMemo(() => {
    if (!categories) return [];

    const buildHierarchy = (parentId: number | null = null, level: number = 0): {
      id: number;
      name: string;
      level: number;
      fullName: string;
    }[] => {
      return categories
        .filter(cat => cat.parent_id === parentId)
        .sort((a, b) => a.display_order - b.display_order)
        .flatMap(category => [
          {
            id: category.id,
            name: category.name,
            level,
            fullName: category.name
          },
          ...buildHierarchy(category.id, level + 1).map(child => ({
            ...child,
            fullName: `${category.name} > ${child.name}`
          }))
        ]);
    };

    return buildHierarchy();
  }, [categories]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
    },
    []
  );

  const handleCategoryChange = useCallback(
    (value: string) => {
      onFiltersChange({
        ...filters,
        categoryId: value === "all" ? undefined : parseInt(value),
      });
    },
    [filters, onFiltersChange]
  );

  const handlePlanChange = useCallback(
    (value: string) => {
      onFiltersChange({
        ...filters,
        planId: value === "all" ? undefined : parseInt(value),
      });
    },
    [filters, onFiltersChange]
  );

  const handleLocalChannelChange = useCallback(
    (checked: boolean) => {
      onFiltersChange({
        ...filters,
        isLocal: checked || undefined,
      });
    },
    [filters, onFiltersChange]
  );

  const handleDuplicateChannelChange = useCallback(
    (checked: boolean) => {
      onFiltersChange({
        ...filters,
        isDuplicate: checked || undefined,
      });
    },
    [filters, onFiltersChange]
  );

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Input
            id="search"
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by name or number..."
            className="w-full"
          />
        </div>

        <div className="w-full sm:w-48">
          <Select
            value={filters.categoryId?.toString() ?? "all"}
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger id="category">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categoryHierarchy.map((category) => (
                <SelectItem
                  key={category.id}
                  value={category.id.toString()}
                  className={`pl-${category.level * 4 + 2}`}
                >
                  {category.fullName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-full sm:w-48">
          <Select
            value={filters.planId?.toString() ?? "all"}
            onValueChange={handlePlanChange}
          >
            <SelectTrigger id="plan">
              <SelectValue placeholder="All plans" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              {plans?.map((plan) => (
                <SelectItem key={plan.id} value={plan.id.toString()}>
                  {plan.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="local-channel"
            checked={!!filters.isLocal}
            onCheckedChange={(checked) => handleLocalChannelChange(checked === true)}
          />
          <Label htmlFor="local-channel" className="text-sm whitespace-nowrap">
            Local Channels
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="duplicate-channel"
            checked={!!filters.isDuplicate}
            onCheckedChange={(checked) => handleDuplicateChannelChange(checked === true)}
          />
          <Label htmlFor="duplicate-channel" className="text-sm whitespace-nowrap">
            Duplicate Channels
          </Label>
        </div>

        {(searchValue || filters.categoryId || filters.planId || filters.isLocal || filters.isDuplicate) && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onFiltersChange({
              search: '',
              categoryId: undefined,
              planId: undefined,
              isLocal: undefined,
              isDuplicate: undefined
            })}
            className="h-9 w-9"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
